import type { ReactNode } from 'react';
import type { LineDelay, PlatformBlock as Block } from './types';
import { delaySec } from './format';
import { Icon } from '../ui/Icon';

/*
 * "On {Platform}" under the graph of a destination card (docs/m15-live-stats.md 1.4,
 * artboard Stats-3): what the platform itself reports. Our numbers stay above it.
 * The block keeps a minimum height so cards in a row line up; a card without a
 * block (custom RTMP) reserves the same space.
 */

const MIN_H = 'min-h-[118px]';

/** 842 · 1 213 · 12.3K — grouped up to 9 999, compact above. */
export function viewersText(n: number): string {
  if (n < 10_000) return n.toLocaleString('en-US').replace(/,/g, ' ');
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 100_000 ? 1 : 0)}K`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

/** 96 · 1.1K — likes and followers. */
function compact(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}K`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

function uptime(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h} h ${m} m` : `${m} m`;
}

export function agoText(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? `${s} s ago` : `${Math.floor(s / 60)} min ago`;
}

/** Watching: the system's eye icon at 12 px (dense rows and the map's head). */
export function EyeIcon() {
  return <Icon name="eye" size={12} />;
}

function HeartIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" aria-hidden="true" className="flex-none">
      <path d="M7 12S1.5 8.6 1.5 5a2.7 2.7 0 0 1 5.5-1 2.7 2.7 0 0 1 5.5 1C12.5 8.6 7 12 7 12z" fill="none" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" aria-hidden="true" className="flex-none">
      <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M7 4v3.2l2 1.3" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function Cell({ icon, value, label }: { icon?: ReactNode; value: string; label: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-px" data-cell={label}>
      <span className="num flex items-center gap-1.5 font-mono text-sm font-medium">{icon}{value}</span>
      <span className="text-[11px] text-muted">{label}</span>
    </div>
  );
}

/** Space a card without a platform block keeps, so the cards of a row stay level. */
export function PlatformSpacer() {
  return <div aria-hidden="true" className={MIN_H} />;
}

export function PlatformBlock({ id, block, now, phoneDelay, connecting, onConnect }: {
  id: number;
  block: Block;
  now: number;
  /** phone only: the line delay goes here instead of on the map (full only) */
  phoneDelay?: LineDelay;
  connecting?: boolean;
  onConnect?: (p: 'youtube' | 'twitch') => void;
}) {
  const { state, label, stats } = block;
  const right =
    state === 'offline' ? <span className="font-mono text-[10px] font-medium text-faint">connected</span>
    : block.updatedAt !== undefined && (state === 'ok' || state === 'issue' || state === 'stale')
      ? <span className={`font-mono text-[10px] font-medium num ${state === 'stale' ? 'text-warn' : 'text-faint'}`}>{agoText(now - block.updatedAt)}</span>
      : null;
  const showStats = (state === 'ok' || state === 'issue' || state === 'stale') && stats;
  const delay = phoneDelay?.kind === 'full' && phoneDelay.totalMs !== undefined ? phoneDelay.totalMs : undefined;

  return (
    <section data-testid={`platform-block-${id}`} data-state={state} aria-label={`On ${label}`}
      className={`flex flex-col gap-2 border-t border-dashed border-line-2 pt-2.5 ${MIN_H}`}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="m-0 font-mono text-[10px] font-semibold tracking-[.12em] text-muted">ON {label.toUpperCase()}</h3>
        {right}
      </div>

      {state === 'unsupported' && <p className="m-0 text-xs text-muted">{block.text}</p>}
      {state === 'offline' && <p className="m-0 text-xs text-muted">{block.text}</p>}
      {state === 'connect' && (
        <>
          <p className="m-0 text-[13px] text-text-soft">See viewers, likes and the delay your audience has.</p>
          <button type="button" disabled={connecting} onClick={() => block.connect && onConnect?.(block.connect)}
            className="min-h-9 self-start text-left text-[13px] font-medium text-accent underline-offset-2 hover:underline disabled:text-faint">
            {connecting ? `Opening ${label}…` : `Connect ${label} account ↗`}
          </button>
        </>
      )}

      {showStats && (
        <div className="grid grid-cols-2 gap-x-2.5 gap-y-2">
          <Cell icon={<EyeIcon />} value={viewersText(stats.viewers)} label="watching" />
          {block.cells.includes('likes') && <Cell icon={<HeartIcon />} value={stats.likes !== undefined ? compact(stats.likes) : '—'} label="likes" />}
          {/* absent fields are not shown; likes is the exception: a channel that hides them gets "—" (UI handoff 3) */}
          {block.cells.includes('uptime') && stats.uptimeS !== undefined && <Cell value={uptime(stats.uptimeS)} label={`uptime by ${label}`} />}
          {block.cells.includes('followers') && stats.newFollowers !== undefined && <Cell value={`+${compact(stats.newFollowers)}`} label="new followers" />}
          {delay !== undefined && <Cell icon={<ClockIcon />} value={delaySec(delay)} label="delay to viewers" />}
        </div>
      )}

      {state === 'stale' && <p className="m-0 text-xs text-warn">{label} API not answering. Showing last known numbers.</p>}
      {(state === 'issue' || state === 'stale') &&
        block.issues.slice(0, 2).map((i, n) => (
          <div key={n} role="status"
            className={`flex gap-1.5 rounded-md border px-2 py-1.5 text-xs ${i.severity === 'error' ? 'border-fail/[.45] text-fail' : 'border-warn/[.45] text-warn'}`}>
            <span aria-hidden="true">▲</span>
            <span className="min-w-0">{i.text}</span>
          </div>
        ))}
    </section>
  );
}
